import { useEffect, useState, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import { PageLayout } from './components/PageLayout';
import NotFound from "./components/NotFound";
import ManageSignature from './admin/manageSignature';
import Landing from './pages/landing';
import NewRequest from './pages/newRequest';
import RequestsList from './pages/myRequests';
import SignedRequestsList from './pages/signedRequests';
import SearchRequest from './admin/searchRequest';
import AllRequests from './admin/allRequests';
import AdminSuccessPage from './admin/SuccessPage';
import SignaturePage from './admin/signature';
import PrivateRoute from './components/privateRoute';
import SuccessPage from './pages/SuccessPage';
import useAffidavitService from './services/affidavitServices';

const ALLOWED_TENANTS = {
    RELIANCE: "a04a5b96-5d86-4d00-b35b-bc7e2a22f7dd",
    MICRODEV: "5d2e66da-54ba-4897-82ee-60eeb8ce5994",
    NJC_LAWYER: "8351e9b0-9e03-4387-ba5b-14f5f5052b78",
    NJC_ADMIN: "a08e751b-2151-47f2-8bb3-6878b4f62df0"
};

const AuthenticatedRoutes = () => {
    const { accounts } = useMsal();
    const navigate = useNavigate();
    const location = useLocation();
    const [userRole, setUserRole] = useState(null);
    const [isAuthorized, setIsAuthorized] = useState(true);
    const [isCheckingAuthorization, setIsCheckingAuthorization] = useState(true); // New state for checking authorization
    const { fetchAllAffidavitData, fetchMyAffidavitData, fetchAllCourts, fetchAllCourtDivisions, fetchAllAdmin } = useAffidavitService();
    const [allAffidavitRequests, setAllAffidavitRequests] = useState(null);
    const [allCourts, setCourts] = useState([]);
    const [allCourtDivisions, setCourtDivisions] = useState([]);
    const [loading, setLoading] = useState(false);
    const initialLoadRef = useRef(true); // Ref to track initial load

    // Check if user is logged in and If user's role is not allowed, redirect to unauthorized page or home
    useEffect(() => {
        const storedRole = localStorage.getItem("userRole");
        if (storedRole) {
            setUserRole(storedRole);
        }

        if (accounts.length > 0) {
            const { tenantId, username: email } = accounts[0];
            // Check if user's organization is allowed
            if (![ALLOWED_TENANTS.RELIANCE, ALLOWED_TENANTS.MICRODEV, ALLOWED_TENANTS.NJC_ADMIN, ALLOWED_TENANTS.NJC_LAWYER].includes(tenantId)) {
                sessionStorage.setItem("Error", "Access Denied: Your organization is not allowed. Kindly sign in with your Legal mail");
                setIsAuthorized(false);
                setIsCheckingAuthorization(false); // Update state
                return;
            }

            const loadAdminAndCheckEmail = async () => {
                role = "superadmin";
                setUserRole(role);
                localStorage.setItem("userRole", role);

                const fetchedAdmin = await LoadAdmin();
                if (!fetchedAdmin || fetchedAdmin.length === 0) {
                    sessionStorage.setItem("Error", "You don't have access to view this app. Kindly contact the administrator");
                    setIsAuthorized(false);
                    setIsCheckingAuthorization(false); // Update state
                    return;
                }
                else {
                    const isAdmin = fetchedAdmin.some(admin => admin.email === email);
                    // Check if user email is allowed in microdev tenant or njc admin tenant
                    if ((tenantId === ALLOWED_TENANTS.MICRODEV || tenantId === ALLOWED_TENANTS.NJC_ADMIN) && isAdmin) {

                        setIsCheckingAuthorization(false); // Update state
                        LoadData(role); // Load data for admin
                    } else {
                        sessionStorage.setItem("Error", "You don't have access to view this app. Kindly contact the administrator");
                        setIsAuthorized(false);
                        setIsCheckingAuthorization(false); // Update state
                        return;
                    }
                }
            };


            let role = "user";
            if (tenantId === ALLOWED_TENANTS.NJC_LAWYER || tenantId === ALLOWED_TENANTS.RELIANCE) {
                setUserRole(role);
                localStorage.setItem("userRole", role);
                setIsCheckingAuthorization(false); // Update state
                LoadData(role); // Only load data if authorized
            } else {
                loadAdminAndCheckEmail();
            }
            //LoadData(role);
        } else {
            localStorage.removeItem("userRole");
            setIsCheckingAuthorization(false); // Update state
        }
    }, [accounts]);

    useEffect(() => {
        if (userRole && isAuthorized) {
            const defaultPage = userRole === "user" ? "/apply-affidavit" : "/search-request";
            if (location.pathname === "/") {
                navigate(defaultPage, { replace: true });
            }
        }
    }, [userRole, location.pathname, navigate, isAuthorized]);

    useEffect(() => {
        if (location.pathname === "/admin/request-list" || location.pathname === "/signed-request-list" || location.pathname === "/request-list") {

            if (initialLoadRef.current) {
                initialLoadRef.current = false; // Set the flag to false after the initial load
            } else {
                LoadData(userRole); // Refresh data on subsequent navigations
            }
        }
    }, [location.pathname, userRole]);

    const LoadData = async (role) => {
        try {
            setLoading(true);
            let fetchedaffidavitrecord = [];
            if (role === "user") {
                fetchedaffidavitrecord = await fetchMyAffidavitData();
                setAllAffidavitRequests(fetchedaffidavitrecord);
            } else if (role === "superadmin") {
                fetchedaffidavitrecord = await fetchAllAffidavitData();
                setAllAffidavitRequests(fetchedaffidavitrecord);
            }

            let isCourtAdmin = false;
            if (role !== "user" && accounts.length > 0) {
                try {
                    const email = accounts[0].username;
                    const adminList = await fetchAllAdmin();
                    const currentAdmin = adminList?.find((admin) => admin.email === email);
                    isCourtAdmin = currentAdmin?.roleId === 2; // AdminRole.CourtAdmin
                } catch (error) {
                    console.error("Failed to determine admin role:", error);
                }
            }

            const isAdminContext = role !== "user";
            let loadAllcourts = await fetchAllCourts(true, isAdminContext);
            if (!isCourtAdmin) {
                loadAllcourts.unshift({ id: 0, courtName: "All" });
            }
            setCourts(loadAllcourts);
            let loadAllCourtDivisions = await fetchAllCourtDivisions(true, isAdminContext);
            setCourtDivisions(loadAllCourtDivisions);
        } catch (error) {
            console.error("Failed to fetch data:", error);
            sessionStorage.setItem("Error", "An error occurred while loading data. Please try again later.");
            setIsAuthorized(false);
            setIsCheckingAuthorization(false); // Update state
            return;
        } finally {
            setLoading(false);
        }
    };

    const LoadAdmin = async () => {
        try {
            let fetchedAdminRecord = await fetchAllAdmin();
            return fetchedAdminRecord;
        } catch (error) {
            console.error("Failed to fetch data:", error);
            sessionStorage.setItem("Error", "An error occurred while loading admin data. Please try again later.");
            setIsAuthorized(false);
            setIsCheckingAuthorization(false); // Update state
            return;
        } finally {
            setLoading(false);
        }
    };

    // Redirect to home if user is not authorized
    useEffect(() => {
        if (!isAuthorized) {
            navigate("/", { replace: true });
        }
    }, [isAuthorized, navigate]);

    // Show loading indicator while checking authorization
    if (isCheckingAuthorization) {
        return (
            <div className="loader-overlay">
                {/* <Spinner /> */}
                <div className="loader"></div>
            </div>
        );
    }

    // Show landing page if user is not authorized
    if (!isAuthorized) {
        return <Landing />;
    }

    if (userRole === null) {
        return (
            <div className="loader-overlay">
                {/* <Spinner /> */}
                <div className="loader"></div>
            </div>
        );
    }

    return (
        <PageLayout userRole={userRole}>
            <Routes>
                <Route path="*" element={<NotFound />} />
                <Route element={<PrivateRoute allowedRoles={["user"]} userRole={userRole} />}>
                    <Route path="/apply-affidavit" element={<NewRequest />} />
                    <Route path="/request-list" element={<RequestsList affidavitRequests={allAffidavitRequests} courts={allCourts} courtDivisions={allCourtDivisions} loading={loading} setLoading={setLoading} />} />
                    <Route path="/signed-request-list" element={<SignedRequestsList affidavitRequests={allAffidavitRequests} courts={allCourts} courtDivisions={allCourtDivisions} loading={loading} setLoading={setLoading} />} />
                    <Route path="/success" element={<SuccessPage />} />
                </Route>
                <Route element={<PrivateRoute allowedRoles={["superadmin"]} userRole={userRole} />}>
                    <Route path="/search-request" element={<SearchRequest />} />
                    <Route path="/admin/request-list" element={<AllRequests affidavitRequests={allAffidavitRequests} courts={allCourts} courtDivisions={allCourtDivisions} loading={loading} setLoading={setLoading} />} />
                    <Route path="/admin/success" element={<AdminSuccessPage />} />
                    <Route path="/signature" element={<SignaturePage />} />
                    <Route path="/manage-signature" element={<ManageSignature />} />
                </Route>
            </Routes>
        </PageLayout>
    );
};

export default AuthenticatedRoutes;